// pages/DataTablePage.js
import { globalState } from "../../framework/globalState.ts";
import { DashboardDataTableHtml } from '../../components/skins/dashboard-data-table.ts';
import { ExpandableDirectory } from '../../components/directory-list.ts';
import { RegisterDictionaryController } from '../../modules/RegisterDictionaryController.ts';
import { RegisterAssignmentModal } from '../../components/RegisterAssignmentModal.ts';
import { AllRegistersManagerModal } from '../../components/AllRegistersManagerModal.ts';
import { DeviceManagerModal } from '../../components/DeviceManagerModal.ts';
import { fetchSiteWithData, fetchRegisterDictionaries, generateDeviceRegister, fetchDevices, updateDevice } from '../../services/api.ts';

let currentDicts = [];
let allRegisters = [];
let currentFilter = { type: 'site' };
let dataTableWarmSnapshot: {
  siteId: string;
  dicts: any[];
  siteData: any;
  refreshedAt: number;
} | null = null;

// LIVE STATUS CACHE
const deviceStatusCache = new Map(); // deviceId → { status, lastSeen }

export default function DataTablePage() {
  const container = document.createElement("div");
  container.classList.add("parent-container");
  container.innerHTML = new DashboardDataTableHtml().datatableskin();

  const site = globalState.currentSite || JSON.parse(localStorage.getItem("currentSite"));
  if (!site) {
    container.innerHTML = "<p>No site selected</p>";
    return container;
  }

  globalState.currentSite = site;

  requestAnimationFrame(async () => {
    const warmSnapshot = dataTableWarmSnapshot && dataTableWarmSnapshot.siteId === site._id
      ? dataTableWarmSnapshot
      : null;

    if (warmSnapshot) {
      applyPageData(container, site, warmSnapshot.dicts, warmSnapshot.siteData);
      return;
    }

    await initPage(container, site);
    // startLiveStatusUpdates(container);
  });

  return container;
}

async function initPage(container, site) {
  try {
    const [dicts, siteData] = await Promise.all([
      fetchRegisterDictionaries(),
      fetchSiteWithData(site._id)
    ]);

    dataTableWarmSnapshot = {
      siteId: site._id,
      dicts,
      siteData,
      refreshedAt: Date.now(),
    };

    applyPageData(container, site, dicts, siteData);
  } catch (err) {
    console.error("DataTable init failed:", err);
    container.querySelector('.container-body').innerHTML = "<p>Failed to load site data</p>";
  }
}

function applyPageData(container, site, dicts, siteData) {
  currentDicts = dicts;
  const dictMap = Object.fromEntries(dicts.map(d => [d.key, d]));
  allRegisters = siteData.devices.flatMap(d =>
    (d.registers || []).map(r => ({ ...r, deviceName: d.name, deviceId: d._id }))
  );

  deviceStatusCache.clear();
  siteData.devices.forEach(d => {
    deviceStatusCache.set(d._id, {
      status: d.status || 'offline',
      lastSeen: d.lastSeen ? new Date(d.lastSeen) : null
    });
  });

  initTree(container, site, siteData.devices, dictMap);
  initHeaderButtons(container, site);
  initTabs(container);

  currentFilter = { type: 'site' };
  renderParametersTable(container);
}

// LIVE HEARTBEAT — EVERY 10 SECONDS
function startLiveStatusUpdates(container) {
  const update = async () => {
    try {
      const response = await fetchDevices(globalState.currentSite._id);
      const devices = await response.json();

      devices.forEach(device => {
        const lastSeen = device.lastSeen ? new Date(device.lastSeen) : null;
        const minutesAgo = lastSeen ? (Date.now() - lastSeen) / 60000 : Infinity;

        let status = 'offline';
        if (device.status === 'maintenance') status = 'maintenance';
        else if (minutesAgo < 1.5) status = 'online';
        else if (minutesAgo < 5) status = 'stale';

        deviceStatusCache.set(device._id, { status, lastSeen });
      });

      // Rebuild only the device list with fresh dots
      const tree = container.querySelector('.directory-list');
      const subList = tree.querySelector('li.has-directory > ul');
      if (subList) {
        subList.innerHTML = devices.map(d => renderDeviceNode(d, currentDicts.reduce((m, v) => (m[v.key] = v, m), {}))).join('');
        new ExpandableDirectory(tree);
      }
    } catch (err) {
      console.warn("Live status update failed:", err);
    }
  };

  update();
  setInterval(update, 10000);
}

// PULSING STATUS DOT — ONLY THING THAT MATTERS
function getStatusIndicator(deviceId) {
  const { status = 'offline' } = deviceStatusCache.get(deviceId) || {};
  const colors = {
    online: '#28a745',
    stale: '#fd7e14',
    offline: '#dc3545',
    maintenance: '#ffc107'
  };
  const pulse = status === 'online' ? 'pulse' : '';

  return `
    <span class="status-dot ${pulse}"
          style="background:${colors[status]}"
          title="${status.charAt(0).toUpperCase() + status.slice(1)}">
    </span>
  `;
}

function initTree(container, site, devices, dictMap) {
  const ul = container.querySelector('.directory-list');
  ul.innerHTML = `
    <li class="has-directory" role="treeitem" aria-expanded="true">
      <div class="directory-header active" data-type="site">
        <span class="arrow iconify" data-icon="nrk:arrow-dropdown"></span>
        <span class="iconify" data-icon="mdi:home"></span>
        <span class="directory-name">${site.name}</span>
      </div>
      <ul class="sub-directory" role="group">
        ${devices.map(device => renderDeviceNode(device, dictMap)).join('')}
      </ul>
    </li>
  `;

  new ExpandableDirectory(ul);
  container.querySelectorAll('.btn-edit-device').forEach(btn => {
    btn.onclick = (e) => {
      e.stopPropagation();
      const deviceId = btn.dataset.deviceId;
      const device = devices.find(d => d._id === deviceId);
      if (device) new DeviceManagerModal(site._id).open(device);
    };
  });

  container.querySelectorAll('.btn-maintenance').forEach(btn => {
    btn.onclick = async (e) => {
      e.stopPropagation();
      const deviceId = btn.dataset.deviceId;
      const device = devices.find(d => d._id === deviceId);
      const newStatus = device.status === 'maintenance' ? 'offline' : 'maintenance';

      if (confirm(`Set "${device.name}" to ${newStatus.toUpperCase()} mode?`)) {
        try {
          await updateDevice(deviceId, { status: newStatus });
          alert(`${device.name} is now in ${newStatus.toUpperCase()} mode`);
          // Trigger live update
          // startLiveStatusUpdates(container);
        } catch (err) {
          alert('Failed: ' + err.message);
        }
      }
    };
  });

  ul.addEventListener('click', (e) => {
    const header = e.target.closest('.directory-header');
    if (!header) return;

    ul.querySelectorAll('.directory-header').forEach(h => h.classList.remove('active'));

    if (header.dataset.type === 'site') {
      currentFilter = { type: 'site' };
    } else if (header.dataset.deviceId) {
      currentFilter = { type: 'device', deviceId: header.dataset.deviceId };
    }

    header.classList.add('active');
    renderParametersTable(container);
  });
}

function renderDeviceNode(device, dictMap) {
  const groups = {};
  (device.registers || []).forEach(reg => {
    if (!reg.dictionaryKey || !dictMap[reg.dictionaryKey]) return;
    const d = dictMap[reg.dictionaryKey];
    const group = d.group || 'Parameters';
    const subgroup = d.subgroup || 'General';
    if (!groups[group]) groups[group] = {};
    if (!groups[group][subgroup]) groups[group][subgroup] = [];
    groups[group][subgroup].push(reg);
  });

  const isMaintenance = device.status === 'maintenance';
  const maintenanceClass = isMaintenance ? 'maintenance-mode' : '';

  return `
    <li class="has-directory" role="treeitem">
      <div class="directory-header ${maintenanceClass}" data-device-id="${device._id}">
        <span class="arrow iconify" data-icon="nrk:arrow-dropdown"></span>
        ${getStatusIndicator(device._id)}
        <span class="directory-name">
          ${device.name} <small>(${device.role.toUpperCase()})</small>
        </span>

        <!-- EDIT BUTTON -->
        <button class="btn-edit-device btn-sm" data-device-id="${device._id}" title="Edit Device">
          <span class="iconify" data-icon="mdi:pencil"></span>
        </button>

        <!-- MAINTENANCE TOGGLE -->
        <button class="btn-maintenance btn-sm ${isMaintenance ? 'active' : ''}" 
                data-device-id="${device._id}" 
                title="${isMaintenance ? 'Exit Maintenance' : 'Set Maintenance'}">
          <span class="iconify" data-icon="mdi:wrench"></span>
        </button>

        <!-- GENERATE REGS -->
        <button class="btn-generate-regs btn-sm" data-device-id="${device._id}" title="Generate standard tags">
          <span class="iconify" data-icon="mdi:auto-fix"></span>
        </button>
      </div>
      <ul class="sub-directory">
        <!-- groups content -->
        ${Object.entries(groups).map(([groupName, subgroups]) => `
          <li class="has-directory">
            <div class="directory-header">
              <span class="arrow iconify"></span>
              <span class="iconify" data-icon="proicons:folder"></span>
              <span>${groupName}</span>
            </div>
            <ul class="sub-directory">
              ${Object.entries(subgroups).map(([subgroupName, regs]) => `
                <li>
                  <div class="directory-header text-muted">
                    <span>${subgroupName}</span>
                  </div>
                  <ul class="sub-directory">
                    ${regs.map(reg => `
                      <li class="file-item">
                        <span>${reg.name}</span>
                        ${reg.unit ? `<small>(${reg.unit})</small>` : ''}
                      </li>
                    `).join('')}
                  </ul>
                </li>
              `).join('')}
            </ul>
          </li>
        `).join('')}
      </ul>
    </li>
  `;
}

function renderParametersTable(container) {
  const tbody = container.querySelector('#tab-parameters tbody');
  if (!tbody) return;

  let filtered = allRegisters;

  if (currentFilter.type === 'device') {
    filtered = allRegisters.filter(r => r.deviceId === currentFilter.deviceId);
  }

  tbody.innerHTML = filtered.map(r => `
    <tr data-id="${r._id}">
      <td class="text-left">
        <strong>${r.name}</strong><br>
        <small class="text-muted">${r.deviceName} • ${r.dictionaryKey || '—'}</small>
      </td>
      <td>${r.value ?? '-'}</td>
      <td>${r.unit || '-'}</td>
      <td>${r.lastUpdated ? new Date(r.lastUpdated).toLocaleString() : '-'}</td>
      <td>—</td>
      <td><button class="icon-btn plot-btn" title="Plot"><span class="iconify" data-icon="mdi:chart-line"></span></button></td>
      <td><button class="icon-btn"><span class="iconify" data-icon="mdi:eye"></span></button></td>
      <td><button class="icon-btn"><span class="iconify" data-icon="mdi:import"></span></button></td>
      <td><button class="icon-btn"><span class="iconify" data-icon="mdi:export"></span></button></td>
      <td><button class="icon-btn"><span class="iconify" data-icon="mdi:star-outline"></span></button></td>
      <td><button class="icon-btn"><span class="iconify" data-icon="mdi:segment"></span></button></td>
      <td><button class="icon-btn info-btn" data-id="${r._id}"><span class="iconify" data-icon="mdi:information-outline"></span></button></td>
    </tr>
  `).join('');

  // Info button → open assignment modal for editing
  tbody.querySelectorAll('.info-btn').forEach(btn => {
    btn.onclick = () => {
      const reg = allRegisters.find(r => r._id === btn.dataset.id);
      if (reg) new RegisterAssignmentModal(reg).open();
    };
  });
}

function initHeaderButtons(container, site) {
  const btnRefresh = container.querySelector('#btn-refresh-tags');
  const btnDict = container.querySelector('#btn-open-dict');
  const btnCreate = container.querySelector('#btn-create-register');
  const btnAssign = container.querySelector('#btn-assign-existing');

  // New: Manage Devices
  const manageBtn = document.createElement('button');
  manageBtn.id = 'btn-manage-devices';
  manageBtn.className = 'btn-secondary';
  manageBtn.innerHTML = '<span class="iconify" data-icon="mdi:chip"></span> Devices';
  container.querySelector('.column-header > div:last-child').appendChild(manageBtn);

  if (btnRefresh) btnRefresh.onclick = () => location.reload();
  if (btnDict) btnDict.onclick = () => new RegisterDictionaryController().open();
  if (btnCreate) btnCreate.onclick = () => new RegisterAssignmentModal().open();
  if (manageBtn) manageBtn.onclick = () => new DeviceManagerModal(site._id).open();

  // Add this button
  const allRegsBtn = document.createElement('button');
  allRegsBtn.id = 'btn-all-registers';
  allRegsBtn.className = 'btn-secondary';
  allRegsBtn.innerHTML = '<span class="iconify" data-icon="mdi:database-search"></span> All Registers';
  container.querySelector('.column-header > div:last-child').appendChild(allRegsBtn);

  allRegsBtn.onclick = () => new AllRegistersManagerModal().open();

  // Generate registers buttons
  container.querySelectorAll('.btn-generate-regs').forEach(btn => {
    btn.onclick = async (e) => {
      e.stopPropagation();
      if (!confirm('Generate standard registers for this device?')) return;
      try {
        await generateDeviceRegister(btn.dataset.deviceId);
        alert('Registers generated successfully!');
        location.reload();
      } catch (err) {
        alert('Failed: ' + err.message);
      }
    };
  });
  // Add Site Maintenance Toggle Button
  const siteMaintenanceBtn = document.createElement('button');
  siteMaintenanceBtn.id = 'btn-site-maintenance';
  siteMaintenanceBtn.className = 'btn-secondary';
  siteMaintenanceBtn.innerHTML = `<span class="iconify" data-icon="mdi:wrench"></span> Site Maintenance`;
  container.querySelector('.column-header > div:last-child').appendChild(siteMaintenanceBtn);

  let siteInMaintenance = site.status === 'maintenance';
  siteMaintenanceBtn.classList.toggle('active', siteInMaintenance);

  siteMaintenanceBtn.onclick = async () => {
    const newStatus = siteInMaintenance ? 'offline' : 'maintenance';
    if (confirm(`Set entire site to ${newStatus.toUpperCase()} mode?`)) {
      try {
        await fetch(`/api/sites/${site._id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: newStatus })
        });
        siteInMaintenance = !siteInMaintenance;
        siteMaintenanceBtn.classList.toggle('active');
        alert(`Site is now in ${newStatus.toUpperCase()} mode`);
        location.reload();
      } catch (err) {
        alert('Failed to update site status');
      }
    }
  };
}

function initTabs(container) {
  container.querySelectorAll('.tab-btn').forEach(btn => {
    btn.onclick = () => {
      container.querySelectorAll('.tab-btn, .tab-content').forEach(el => el.classList.remove('active'));
      btn.classList.add('active');
      const tab = container.querySelector(`#tab-${btn.dataset.tab}`);
      if (tab) tab.classList.add('active');
    };
  });
}